import React from 'react';
import { X, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

interface MarketStandardsAgreementModalProps {
  isOpen: boolean;
  onAgree: () => void;
  onClose?: () => void; // Optional - if not provided, modal cannot be closed without agreeing
}

export function MarketStandardsAgreementModal({
  isOpen,
  onAgree,
  onClose,
}: MarketStandardsAgreementModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <FileText className="w-6 h-6 text-indigo-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Market Standards Agreement</h2>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            Before creating a market, you must read and agree to the{' '}
            <Link
              to="/market-standards"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 underline font-medium"
            >
              Wayja Prediction Market Standards
            </Link>
            .
          </p>
          <p className="text-sm text-gray-600">
            These standards outline the rules, regulations, and resolution criteria for all markets on Wayja. 
            By clicking "I Agree" below, you confirm that you have read and understood these standards.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Markets that do not comply with these standards may be rejected by administrators.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={onAgree}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium"
          >
            I Agree
          </button>
        </div>
      </div>
    </div>
  );
}

